import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Pin,
  Archive,
  Send
} from 'lucide-react'
import { announcementApi, type Announcement, type AnnouncementStatus, type AppType } from '@/api/announcements'

const STATUS_OPTIONS: { value: AnnouncementStatus | ''; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'draft', label: '초안' },
  { value: 'published', label: '게시중' },
  { value: 'scheduled', label: '예약' },
  { value: 'archived', label: '보관' },
]

const STATUS_BADGES: Record<AnnouncementStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: '초안' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: '게시중' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', label: '예약' },
  archived: { bg: 'bg-amber-100', text: 'text-amber-700', label: '보관' },
}

export function AnnouncementListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filterStatus, setFilterStatus] = useState<AnnouncementStatus | ''>('')
  const [activeAppType, setActiveAppType] = useState<AppType>('company')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'announcements', { page, status: filterStatus, appType: activeAppType }],
    queryFn: () => announcementApi.getList({
      page,
      size: 20,
      status: filterStatus || undefined,
      appType: activeAppType,
    }),
    staleTime: 30 * 1000,
  })

  const deleteMutation = useMutation({
    mutationFn: announcementApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] }),
  })

  const publishMutation = useMutation({
    mutationFn: announcementApi.publish,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] }),
  })

  const archiveMutation = useMutation({
    mutationFn: announcementApi.archive,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] }),
  })

  const handleDelete = (announcement: Announcement) => {
    if (confirm(`정말 "${announcement.title}" 공지사항을 삭제하시겠습니까?`)) {
      deleteMutation.mutate(announcement.announcementId)
    }
  }

  const handlePublish = (announcement: Announcement) => {
    if (confirm(`"${announcement.title}" 공지사항을 게시하시겠습니까?`)) {
      publishMutation.mutate(announcement.announcementId)
    }
  }

  const handleArchive = (announcement: Announcement) => {
    if (confirm(`"${announcement.title}" 공지사항을 보관하시겠습니까?`)) {
      archiveMutation.mutate(announcement.announcementId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">공지사항 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-blue-600" />
            공지사항 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalCount ?? 0}개의 공지사항</p>
        </div>
        <button
          onClick={() => navigate(`/announcements/new?appType=${activeAppType}`)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 공지 작성
        </button>
      </div>

      {/* App Type Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-0" aria-label="App type tabs">
          {([
            { value: 'company' as AppType, label: '기업 B2B' },
            { value: 'lc' as AppType, label: '러닝센터 B2B' },
            { value: 'b2c' as AppType, label: 'B2C' },
          ]).map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setActiveAppType(tab.value); setPage(0) }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeAppType === tab.value
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            className="input w-40"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value as AnnouncementStatus | '')
              setPage(0)
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">제목</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">상단고정</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">작성자</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">조회수</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">생성일</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.announcements.map((announcement) => (
                <tr
                  key={announcement.announcementId}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/announcements/${announcement.announcementId}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {announcement.isPinned && (
                        <Pin className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-slate-900 truncate max-w-[300px]">
                        {announcement.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGES[announcement.status].bg} ${STATUS_BADGES[announcement.status].text}`}>
                      {STATUS_BADGES[announcement.status].label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {announcement.isPinned ? (
                      <span className="text-amber-500">Y</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">{announcement.authorName}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 text-right">{announcement.viewCount.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(announcement.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => navigate(`/announcements/${announcement.announcementId}`)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title="상세보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {announcement.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(announcement)}
                          className="p-2 hover:bg-green-50 rounded-lg text-green-600"
                          title="게시"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {announcement.status === 'published' && (
                        <button
                          onClick={() => handleArchive(announcement)}
                          className="p-2 hover:bg-amber-50 rounded-lg text-amber-600"
                          title="보관"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/announcements/${announcement.announcementId}/edit`)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title="수정"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.announcements.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    공지사항이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalCount)} / {data.totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages - 1}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
